import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import CopyToClipboard from "@components/copy_clipboard"
import DialogProvider from "@components/dialog_provider"
import { useCustomerPageContext } from "@contexts/customer"
import { resetPassword } from "@domain/auth"
import { VerifyUserEmail } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { Box, IconButton, Paper } from "@mui/material"
import { generateRandomPassword } from "@utils/functions/generate_random_password"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { CustomerPerson } from "__generated__/graphql"
export default function ResetPassword({
  isOpen,
  setClose,
  selectedRow
}: {
  isOpen: boolean
  setClose: () => void
  selectedRow?: CustomerPerson
}) {
  const { translate } = useCustomerPageContext()
  const [loading, setLoading] = useState(false)

  const [randomPassword, setRandomPassword] = useState<string>("")

  const {
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<VerifyUserEmail>({
    mode: "all"
  })
  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit() {
    if (!selectedRow?._id) {
      return
    }
    setLoading(true)
    const { isSuccess, errorMessage } = await resetPassword({
      _id: selectedRow._id,
      customerId: selectedRow.customer,
      password: randomPassword
    })
    if (!isSuccess && !!errorMessage) {
      setLoading(false)
      showErrorToast(errorMessage)
      return
    }
    showSuccessToast("password_resetted")
    setLoading(false)
    handleClose()
  }

  useEffect(() => {
    handleGenerateRandomPassword()
  }, [])

  const handleGenerateRandomPassword = () => {
    const password = generateRandomPassword(8)
    setRandomPassword(password)
  }

  return (
    <DialogProvider
      title={translate("reset_password")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("reset"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack
        justifyContent={"center"}
        alignItems={"center"}
        flex={1}
        gap={2.25}
      >
        <Typography align="center" sx={{ maxWidth: 360 }}>
          {translate("reset_password_description")}
        </Typography>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: 1.25,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.default"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 1.25,
              px: 1.5,
              py: 1,
              gap: 1,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider"
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.08em",
                lineHeight: 1.2,
                wordBreak: "break-all"
              }}
            >
              {randomPassword}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ display: "inline-flex" }}>
                <CopyToClipboard text={randomPassword} />
              </Box>
              <IconButton
                onClick={handleGenerateRandomPassword}
                size="small"
                sx={{ p: 0.5 }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Stack>
    </DialogProvider>
  )
}
